import { ItemModel } from "../../../../app/data/common/models";
import {
  BolAddressBlock,
  BolDocument,
  BolFieldValue,
  BolItem,
  FinalBOLResult,
  fieldHasDisplayValue,
} from "../../../../app/data/pickup-requests/bolModels";
import { mergeHumanFieldValue } from "./bolFieldBadges";
import { resolveShipperEmailFallback } from "./bolShipperEmailUtils";
import { bolDateToApiFormat, convertToAmPm, safeBolDate, safeBolTime } from "./bolTimeUtils";

const fieldStr = (f?: BolFieldValue | null): string =>
  f?.value != null ? String(f.value) : "";

export interface BolReviewAddressForm {
  name: string;
  addressLine_1: string;
  addressLine_2: string;
  city: string;
  state: string;
  zip: string;
  phoneNumber: string;
  email: string;
}

export interface BolReviewItemForm {
  packageType: string;
  numberOfUnits: string;
  weight: string;
  ratingClass: string;
  description: string;
  length: string;
  width: string;
  height: string;
  yards: string;
  notes: string;
  sidemark: string;
}

export interface BolReviewFormValues {
  bolNumber: string;
  poNumber: string;
  additionalReference: string;
  pickupDate: string;
  pickupReadyTime: string;
  pickupCloseTime: string;
  specialInstructions: string;
  pickupFrom: BolReviewAddressForm;
  shipTo: BolReviewAddressForm;
  items: BolReviewItemForm[];
}

const emptyAddress = (): BolReviewAddressForm => ({
  name: "",
  addressLine_1: "",
  addressLine_2: "",
  city: "",
  state: "",
  zip: "",
  phoneNumber: "",
  email: "",
});

const addressFromBlock = (block?: BolAddressBlock): BolReviewAddressForm => ({
  name: fieldStr(block?.name),
  addressLine_1: fieldStr(block?.addressLine_1),
  addressLine_2: fieldStr(block?.addressLine_2),
  city: fieldStr(block?.city),
  state: fieldStr(block?.state),
  zip: fieldStr(block?.zip),
  phoneNumber: fieldStr(block?.phoneNumber),
  email: fieldStr(block?.email),
});

const itemFromBol = (item?: BolItem): BolReviewItemForm => ({
  packageType: fieldStr(item?.type) || "PALLET",
  numberOfUnits: fieldStr(item?.numberOfUnits),
  weight: fieldStr(item?.weight),
  ratingClass: fieldStr(item?.classCode),
  description: fieldStr(item?.description),
  length: fieldStr(item?.length),
  width: fieldStr(item?.width),
  height: fieldStr(item?.height),
  yards: fieldStr(item?.squareYards),
  notes: fieldStr(item?.notes),
  sidemark: fieldStr(item?.sidemark),
});

export const bolDocToFormValues = (
  doc: BolDocument,
  options?: { senderEmail?: string }
): BolReviewFormValues => {
  const pickupFrom = addressFromBlock(doc.pickupFrom);
  pickupFrom.email = resolveShipperEmailFallback(pickupFrom.email, options?.senderEmail);

  return {
    bolNumber: fieldStr(doc.bol),
    poNumber: fieldStr(doc.purchaseOrder),
    additionalReference: fieldStr(doc.orderDescription),
    pickupDate: safeBolDate(fieldStr(doc.pickupWindow?.windowDate)),
    pickupReadyTime: safeBolTime(fieldStr(doc.pickupWindow?.windowStartTime)),
    pickupCloseTime: safeBolTime(fieldStr(doc.pickupWindow?.windowEndTime)),
    specialInstructions: fieldStr(doc.generalNotes),
    pickupFrom,
    shipTo: addressFromBlock(doc.shipTo),
    items: (doc.items?.length ? doc.items : [{}]).map(itemFromBol),
  };
};

export const applyShipperEmailFallbackToBolDocument = (
  doc: BolDocument,
  senderEmail?: string
): BolDocument => {
  const aiEmail = fieldStr(doc.pickupFrom?.email);
  const fallback = resolveShipperEmailFallback(aiEmail, senderEmail);
  if (!fallback || fallback === aiEmail) return doc;

  const next = { ...doc, pickupFrom: { ...doc.pickupFrom } };
  next.pickupFrom!.email = mergeHumanFieldValue(doc.pickupFrom?.email, fallback);
  return next;
};

export const createEmptyBolSnapshot = (): FinalBOLResult => ({
  document_id: "",
  source_system: "manual-entry",
  shipper_id: "",
  customer_id: "",
  item_signature: "",
  bol_document: {},
  overall_confidence: "LOW",
  auto_action: "Human verification required",
  context_sources_used: [],
});

export const emptyBolReviewFormValues = (): BolReviewFormValues => ({
  bolNumber: "",
  poNumber: "",
  additionalReference: "",
  pickupDate: "",
  pickupReadyTime: "",
  pickupCloseTime: "",
  specialInstructions: "",
  pickupFrom: emptyAddress(),
  shipTo: emptyAddress(),
  items: [itemFromBol()],
});

const normalizeFormFieldValue = (value: unknown): string => {
  if (value == null) return "";
  return String(value);
};

const preserveOrMerge = (
  existing: BolFieldValue | undefined,
  value: unknown
): BolFieldValue => {
  const trimmed = normalizeFormFieldValue(value).trim();
  if (!trimmed) {
    return mergeHumanFieldValue(existing, "");
  }
  if (existing && fieldHasDisplayValue(existing) && !existing.humanProvided) {
    const unchanged = String(existing.value).trim() === trimmed;
    if (unchanged) return existing;
  }
  return mergeHumanFieldValue(existing, trimmed);
};

const addressToBlock = (
  form: BolReviewAddressForm,
  existing?: BolAddressBlock
): BolAddressBlock => ({
  name: preserveOrMerge(existing?.name, form.name),
  addressLine_1: preserveOrMerge(existing?.addressLine_1, form.addressLine_1),
  addressLine_2: preserveOrMerge(existing?.addressLine_2, form.addressLine_2),
  city: preserveOrMerge(existing?.city, form.city),
  state: preserveOrMerge(existing?.state, form.state),
  zip: preserveOrMerge(existing?.zip, form.zip),
  phoneNumber: preserveOrMerge(existing?.phoneNumber, form.phoneNumber),
  email: preserveOrMerge(existing?.email, form.email),
});

const itemToBol = (form: BolReviewItemForm, existing?: BolItem): BolItem => ({
  type: preserveOrMerge(existing?.type, form.packageType),
  numberOfUnits: preserveOrMerge(existing?.numberOfUnits, form.numberOfUnits),
  weight: preserveOrMerge(existing?.weight, form.weight),
  classCode: preserveOrMerge(existing?.classCode, form.ratingClass),
  description: preserveOrMerge(existing?.description, form.description),
  length: preserveOrMerge(existing?.length, form.length),
  width: preserveOrMerge(existing?.width, form.width),
  height: preserveOrMerge(existing?.height, form.height),
  squareYards: preserveOrMerge(existing?.squareYards, form.yards),
  notes: preserveOrMerge(existing?.notes, form.notes),
  sidemark: preserveOrMerge(existing?.sidemark, form.sidemark),
});

export const formValuesToBolDocument = (
  values: BolReviewFormValues,
  existingDoc: BolDocument
): BolDocument => ({
  ...existingDoc,
  bol: preserveOrMerge(existingDoc.bol, values.bolNumber),
  purchaseOrder: preserveOrMerge(existingDoc.purchaseOrder, values.poNumber),
  orderDescription: preserveOrMerge(existingDoc.orderDescription, values.additionalReference),
  generalNotes: preserveOrMerge(existingDoc.generalNotes, values.specialInstructions),
  pickupWindow: {
    windowDate: preserveOrMerge(
      existingDoc.pickupWindow?.windowDate,
      bolDateToApiFormat(values.pickupDate)
    ),
    windowStartTime: preserveOrMerge(
      existingDoc.pickupWindow?.windowStartTime,
      convertToAmPm(values.pickupReadyTime) || values.pickupReadyTime
    ),
    windowEndTime: preserveOrMerge(
      existingDoc.pickupWindow?.windowEndTime,
      convertToAmPm(values.pickupCloseTime) || values.pickupCloseTime
    ),
    timeZoneOffset: existingDoc.pickupWindow?.timeZoneOffset,
  },
  pickupFrom: addressToBlock(values.pickupFrom, existingDoc.pickupFrom),
  shipTo: addressToBlock(values.shipTo, existingDoc.shipTo),
  items: values.items.map((item, idx) => itemToBol(item, existingDoc.items?.[idx])),
});

export const syncFormToEditedBol = (
  values: BolReviewFormValues,
  editedBol: FinalBOLResult
): FinalBOLResult => ({
  ...editedBol,
  bol_document: formValuesToBolDocument(values, editedBol.bol_document),
});

export const itemFormToItemModel = (item: BolReviewItemForm): ItemModel => ({
  packageType: item.packageType || "PALLET",
  commodityCode: undefined,
  units: item.numberOfUnits ? Number(item.numberOfUnits) : ("" as unknown as number),
  yards: item.yards ? Number(item.yards) : ("" as unknown as number),
  weight: item.weight ? Number(item.weight) : ("" as unknown as number),
  length: item.length || "",
  width: item.width || "",
  diameter: "",
  height: item.height || "",
  ratingClass: item.ratingClass || "",
  sidemark: item.sidemark || "",
  notes: item.notes || "",
});
